import type { Visualizer } from './types';
import { clearBg, drawHud } from './types';

type Star = {
  x: number;
  y: number;
  z: number;
  pz: number;
  hue: number;
  bin: number;
  twinkle: number;
};

const stars: Star[] = [];
let lastW = 0;
let lastH = 0;

function ensure(count: number, w: number, h: number): void {
  if (w !== lastW || h !== lastH) {
    stars.length = 0;
    lastW = w;
    lastH = h;
  }
  while (stars.length < count) {
    stars.push(spawn(stars.length));
  }
  while (stars.length > count) stars.pop();
}

function spawn(i: number): Star {
  return {
    x: Math.random() * 2 - 1,
    y: Math.random() * 2 - 1,
    z: Math.random() * 0.98 + 0.02,
    pz: 1,
    hue: 145 + Math.random() * 70,
    bin: i % 128,
    twinkle: Math.random() * Math.PI * 2,
  };
}

export const starfieldViz: Visualizer = {
  id: 'starfield',
  label: 'Starfield',
  draw(frame) {
    const { ctx, w, h, bands, freq, t } = frame;
    // trail fade — stronger on low energy so streaks read on bass hits
    clearBg(ctx, w, h, 0.28 + bands.energy * 0.12);

    const count = Math.floor(140 + bands.energy * 320 + bands.highs * 80);
    ensure(count, w, h);

    const cx = w / 2 + Math.sin(t * 0.7) * bands.mids * 18;
    const cy = h / 2 + Math.cos(t * 0.55) * bands.bass * 14;
    // big music coupling
    const speed =
      0.006 +
      bands.bass * 0.055 +
      bands.kick * 0.07 +
      bands.beat * 0.05 +
      bands.energy * 0.02;
    const stretch = 0.15 + bands.bass * 0.55 + bands.kick * 0.7 + bands.beat * 0.45;
    const sizeBoost = 1 + bands.highs * 1.4 + bands.energy * 0.6;
    const hueShift = bands.mids * 55 + bands.highs * 35 + t * 12;

    // vignette pulse on kick
    if (bands.kick > 0.25 || bands.beat > 0.2) {
      const flash = Math.max(bands.kick, bands.beat);
      const g = ctx.createRadialGradient(cx, cy, 2, cx, cy, Math.min(w, h) * (0.2 + flash * 0.35));
      g.addColorStop(0, `rgba(255,255,255,${0.08 + flash * 0.22})`);
      g.addColorStop(0.35, `rgba(86,240,200,${0.05 + flash * 0.14})`);
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
    }

    for (let i = 0; i < stars.length; i++) {
      const s = stars[i];
      const f = freq[s.bin % freq.length] / 255;
      const f2 = freq[(s.bin * 3) % freq.length] / 255;
      s.pz = s.z;
      s.z -= speed * (0.45 + f * 1.35 + bands.energy * 0.4);

      if (s.z <= 0.02) {
        const ns = spawn(i);
        s.x = ns.x;
        s.y = ns.y;
        s.z = 1;
        s.pz = 1;
        s.hue = ns.hue;
        s.bin = ns.bin;
        s.twinkle = ns.twinkle;
      }

      const k = 0.72 / s.z;
      const pk = 0.72 / s.pz;
      const x = cx + s.x * k * w * 0.48;
      const y = cy + s.y * k * h * 0.48;
      const px = cx + s.x * pk * w * 0.48;
      const py = cy + s.y * pk * h * 0.48;

      if (x < -40 || x > w + 40 || y < -40 || y > h + 40) continue;

      const near = 1 - s.z;
      const tw =
        0.55 +
        0.45 * Math.sin(t * (6 + f2 * 10) + s.twinkle) * (0.35 + bands.highs);
      const size = near * (1.4 + f * 3.2) * sizeBoost * tw;
      const alpha = (0.2 + near * 0.75) * (0.45 + f * 0.55) * tw;

      // hyperspace streak — length scales hard with bass/kick
      const streakAlpha = Math.min(0.95, 0.08 + stretch * (0.35 + f) + near * 0.25);
      if (stretch > 0.2 || near > 0.45) {
        ctx.strokeStyle = `hsla(${s.hue + hueShift * 0.3}, 95%, ${60 + near * 25}%, ${streakAlpha})`;
        ctx.lineWidth = Math.max(0.6, size * 0.55);
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(x, y);
        ctx.stroke();
      }

      ctx.beginPath();
      ctx.fillStyle = `hsla(${s.hue + hueShift + f2 * 30}, 95%, ${52 + near * 35 + bands.highs * 10}%, ${alpha})`;
      ctx.arc(x, y, Math.max(0.5, size), 0, Math.PI * 2);
      ctx.fill();
    }

    // center warp core
    const coreR = Math.min(w, h) * (0.12 + bands.energy * 0.18 + bands.kick * 0.1);
    const core = ctx.createRadialGradient(cx, cy, 1, cx, cy, coreR);
    core.addColorStop(0, `rgba(255,255,255,${0.1 + bands.kick * 0.35 + bands.beat * 0.15})`);
    core.addColorStop(0.25, `rgba(86,240,200,${0.08 + bands.energy * 0.18})`);
    core.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = core;
    ctx.fillRect(0, 0, w, h);

    drawHud(frame);
  },
};
